import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { IdentificationCard, Plus } from "@phosphor-icons/react";

export default function Visitors() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    name: "", mobile: "", plate_number: "", unit_number: user?.unit_number || "",
    purpose: "", expected_arrival: "", expected_duration_hours: 4,
  });
  const [showQR, setShowQR] = useState(null);

  const load = () => api.get(`/visitors${user?.role === "owner" || user?.role === "tenant" ? "?mine=true" : ""}`).then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/visitors", {
        property_id: user?.property_id,
        ...form,
        expected_duration_hours: Number(form.expected_duration_hours) || 4,
      });
      toast.success("Visitor pre-registered");
      setShowQR(r.data);
      setForm({ ...form, name: "", mobile: "", plate_number: "", purpose: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Visitors</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">Pre-register guests.</h1>
        <p className="text-sm text-zinc-500 mt-1">Generates a QR pass guards can scan on arrival.</p>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-[#141414] border-zinc-800 p-6">
          <div className="flex items-center gap-2">
            <Plus size={20} className="text-emerald-400" />
            <h3 className="font-heading text-xl font-bold">New Pre-Registration</h3>
          </div>
          <form className="mt-4 space-y-3" onSubmit={submit}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Visitor Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-[#0F0F10] border-zinc-800" data-testid={TID.visitorName} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Mobile</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-[#0F0F10] border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plate Number</Label>
                <Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value.toUpperCase() })} required className="bg-[#0F0F10] border-zinc-800 font-mono" data-testid={TID.visitorPlate} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Unit Visiting</Label>
                <Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} className="bg-[#0F0F10] border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Expected Arrival</Label>
                <Input type="datetime-local" value={form.expected_arrival} onChange={(e) => setForm({ ...form, expected_arrival: e.target.value })} className="bg-[#0F0F10] border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Duration (hrs)</Label>
                <Input type="number" value={form.expected_duration_hours} onChange={(e) => setForm({ ...form, expected_duration_hours: e.target.value })} className="bg-[#0F0F10] border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Purpose</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Family visit, delivery, meeting…" className="bg-[#0F0F10] border-zinc-800" />
            </div>
            <Button type="submit" className="w-full min-h-[44px] bg-white text-black hover:bg-zinc-200" data-testid={TID.visitorCreate}>Generate QR Pass</Button>
          </form>
        </Card>

        <Card className="bg-[#141414] border-zinc-800 p-6">
          <div className="flex items-center gap-2">
            <IdentificationCard size={20} className="text-purple-400" />
            <h3 className="font-heading text-xl font-bold">{showQR ? "Visitor Pass" : "Recent Visitors"}</h3>
          </div>

          {showQR ? (
            <div className="mt-4 flex flex-col items-center gap-3">
              <div className="bg-white p-4 rounded-md">
                <QRCodeSVG value={`CPCC-VISITOR:${showQR.pass_code}`} size={200} />
              </div>
              <p className="font-mono text-lg">{showQR.plate_number}</p>
              <p className="text-sm text-zinc-400">{showQR.name} · unit {showQR.unit_number}</p>
              <p className="text-xs font-mono text-zinc-500">{showQR.pass_code}</p>
              <Button variant="outline" onClick={() => setShowQR(null)} className="border-zinc-700 bg-transparent">Done</Button>
            </div>
          ) : (
            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
              {list.map(v => (
                <div key={v.id} className="bg-[#0F0F10] border border-zinc-800 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{v.name}</p>
                      <p className="text-xs text-zinc-500 font-mono">{v.plate_number} · unit {v.unit_number || "—"}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded border ${
                      v.status === "registered" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" :
                      v.status === "arrived" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                      "bg-zinc-700/30 text-zinc-400 border-zinc-700"
                    }`}>{v.status}</span>
                  </div>
                  <button onClick={() => setShowQR(v)} className="mt-2 text-xs text-emerald-400 hover:underline">Show QR pass</button>
                </div>
              ))}
              {list.length === 0 && <p className="text-sm text-zinc-500">No visitors yet.</p>}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
